import { ChatTeardropText, Plus } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { feedbackListQuery } from "@/components/feedback/api";
import { FeedbackQueue } from "@/components/feedback/FeedbackQueue";
import { Testers, useFeedbackProducts } from "@/components/feedback/Testers";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import { Button, Select, segmentItem, segmentItemActive, segmentTrack } from "@/components/ui";
import { cx } from "@/lib/cx";
import { shortAge } from "@/lib/format";

type View = "open" | "resolved" | "testers";
type FeedbackSearch = { view?: Exclude<View, "open">; product?: string; tester?: string; create?: "1" };

/** /feedback: open points (default), `?view=resolved`, `?view=testers`; `?product=`, `?tester=` filter the queue. */
export const Route = createFileRoute("/_app/feedback")({
  validateSearch: (s: Record<string, unknown>): FeedbackSearch => ({
    view: s.view === "resolved" || s.view === "testers" ? s.view : undefined,
    product: typeof s.product === "string" && s.product ? s.product : undefined,
    tester: typeof s.tester === "string" && s.tester ? s.tester : undefined,
    create: s.create === "1" || s.create === 1 ? "1" : undefined,
  }),
  component: FeedbackPage,
});

const ALL = "__all";

function FeedbackPage() {
  useBreadcrumbs([{ label: m.nav_feedback() }]);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/feedback" });
  const view: View = search.view ?? "open";
  const products = useFeedbackProducts();
  // The open list feeds the header meta (count, oldest) whichever view is shown.
  const open = useQuery(feedbackListQuery("open")).data;
  const setSearch = (patch: Partial<FeedbackSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const openCreate = () => void navigate({ search: { view: "testers", create: "1" } });

  const meta =
    open && open.openCount > 0
      ? open.oldestOpenAt
        ? m.fb_meta_open_oldest({ count: open.openCount, age: shortAge(open.oldestOpenAt) })
        : m.fb_meta_open({ count: open.openCount })
      : m.fb_meta();

  const views: { id: View; label: string }[] = [
    { id: "open", label: m.fb_view_open() },
    { id: "resolved", label: m.fb_view_resolved() },
    { id: "testers", label: m.fb_view_testers() },
  ];

  return (
    <Page
      title={m.nav_feedback()}
      icon={ChatTeardropText}
      meta={meta}
      width="full"
      className="max-w-4xl"
      actions={
        <Button variant={view === "testers" ? "primary" : "secondary"} onPress={openCreate}>
          <Plus size={14} aria-hidden="true" />
          {m.fb_link_create()}
        </Button>
      }
      toolbar={
        <div className="flex flex-wrap items-center gap-3">
          <nav aria-label={m.fb_views_label()} className={cx(segmentTrack, "w-fit")}>
            {views.map((v) => (
              <Link
                key={v.id}
                to="/feedback"
                search={{ view: v.id === "open" ? undefined : v.id }}
                aria-current={view === v.id ? "page" : undefined}
                className={cx(segmentItem, view === v.id && segmentItemActive)}
              >
                {v.label}
                {v.id === "open" && open?.openCount ? (
                  <span className="ml-1.5 text-xs text-fg-muted tnum">{open.openCount}</span>
                ) : null}
              </Link>
            ))}
          </nav>
          {view !== "testers" && open ? (
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              {open.links.length > 1 ? (
                <Select
                  label={m.fb_filter_tester()}
                  labelHidden
                  className="w-44"
                  items={[
                    { id: ALL, label: m.fb_filter_all_testers() },
                    ...open.links.map((l) => ({ id: l.id, label: l.label })),
                  ]}
                  selectedKey={search.tester ?? ALL}
                  onSelectionChange={(k) => setSearch({ tester: k === ALL ? undefined : k })}
                />
              ) : null}
              {products.length > 1 ? (
                <Select
                  label={m.fb_filter_app()}
                  labelHidden
                  className="w-44"
                  items={[
                    { id: ALL, label: m.fb_filter_all_apps() },
                    ...products.map((p) => ({ id: p.id, label: p.name })),
                  ]}
                  selectedKey={search.product ?? ALL}
                  onSelectionChange={(k) => setSearch({ product: k === ALL ? undefined : k })}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      }
    >
      {view === "testers" ? (
        <Testers
          createOpen={search.create === "1"}
          onCreateOpenChange={(o) => setSearch({ create: o ? "1" : undefined })}
        />
      ) : (
        <FeedbackQueue
          state={view === "resolved" ? "resolved" : "open"}
          productId={search.product}
          linkId={search.tester}
          onCreateLink={openCreate}
        />
      )}
    </Page>
  );
}
